import React, { useState, useImperativeHandle, forwardRef } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import "./PatientProfileModal.scss";

import { RootState } from "../../../store/store";
import { formatDateTime, getAgeByDob } from "../../../utils/date-and-time";
import { getInitials } from "../../../utils/common";
import { getIndication, getPlanStatus } from "../../../utils/patient";

import CopyButton from "../../../atoms/CopyButton/CopyButton";
import CurrentOfferings from "../CurrentOfferings/CurrentOfferings";
import PatientConditionCard, {
  conditionDraftToAnswers,
} from "../PatientConditionCard/PatientConditionCard";
import { useSharedProfile } from "../../../organisms/PatientDetailsV2/SharedProfile/useSharedProfile";

import { ReactComponent as CloseIcon } from "../../../assets/icons/patient-layout-v2/modal/CloseIcon.svg";
import { ReactComponent as IdCardIcon } from "../../../assets/icons/patient-layout-v2/modal/IdCardIcon.svg";
import { ReactComponent as ShieldIcon } from "../../../assets/icons/patient-layout-v2/modal/ShieldIcon.svg";
import { ReactComponent as ListIcon } from "../../../assets/icons/patient-layout-v2/modal/ListIcon.svg";
import { ReactComponent as PulseIcon } from "../../../assets/icons/patient-layout-v2/overview/PulseIcon.svg";

interface Props {
  onConditionSaved?: () => void;
}

const getPlanDuration = (days?: number | null) => {
  if (!days) return null;
  if (days >= 30) {
    const months = Math.floor(days / 30);
    return `${months} Month${months > 1 ? "s" : ""}`;
  }
  return `${days} Day${days > 1 ? "s" : ""}`;
};

interface InfoRowProps {
  label: string;
  value?: string | number | null;
  href?: string;
  copyable?: boolean;
}

const InfoRow = ({ label, value, href, copyable }: InfoRowProps) => (
  <div className="mc-row">
    <span className="mc-k">{label}</span>
    <span className="mc-v-group">
      {href && value ? (
        <a className="mc-v mc-link" href={href}>
          {value}
        </a>
      ) : (
        <span className={`mc-v ${value ? "" : "empty"}`}>{value || "—"}</span>
      )}
      {copyable && (
        <CopyButton value={value} title={`Copy ${label.toLowerCase()}`} />
      )}
    </span>
  </div>
);

const PatientProfileModal = forwardRef<any, Props>(({ onConditionSaved }, ref) => {
  const [showModal, setShowModal] = useState(false);
  const { patientDetails } = useSelector((state: RootState) => state.patient);
  const patientId =
    patientDetails?.data?.patient_id ||
    localStorage.getItem("patient_id") ||
    undefined;
  const profile = useSharedProfile(patientId, { enabled: showModal });

  useImperativeHandle(ref, () => ({
    showPatientDetails() {
      setShowModal(true);
    },

    hidePatientDetails() {
      setShowModal(false);
    },
  }));

  if (!showModal) return null;

  const data = patientDetails?.data;

  const patientName = data?.name || "No patient loaded";
  const patientGender =
    data?.gender === "M" ? "Male" : data?.gender === "F" ? "Female" : "";
  const patientAge = getAgeByDob(data?.dob ?? null);

  const subParts = [
    data?.patient_id ? `ID ${data.patient_id}` : null,
    patientAge ? `${patientAge} yrs${patientGender ? ` · ${patientGender}` : ""}` : null,
    data?.city || null,
  ].filter(Boolean) as string[];

  const planStatus = data ? getPlanStatus(data) : null;

  const height = data?.height ? `${data.height} ${data?.height_unit || ""}`.trim() : null;
  const weight = data?.weight ? `${data.weight} ${data?.weight_unit || ""}`.trim() : null;
  const bmi =
    data?.height && data?.weight
      ? (
          Number(data.weight) / Math.pow(Number(data.height) / 100, 2)
        ).toFixed(1)
      : null;

  const condition = getIndication(data);

  const phoneDisplay = data?.contact_no
    ? `${data?.country_code || ""} ${data.contact_no}`.trim()
    : null;
  const phoneHref = data?.contact_no
    ? `tel:${(data?.country_code || "").replace(/\s/g, "")}${data.contact_no}`
    : undefined;
  const emailHref = data?.email ? `mailto:${data.email}` : undefined;

  const asDate = (timestamp?: string | null) =>
    timestamp
      ? formatDateTime({ timestamp, format: 12, showTime: false })
      : null;

  const planStart = asDate(data?.plan_start_datetime);
  const planEnd = asDate(data?.plan_expiry_datetime || data?.expiry_date);

  // The API sends this as a string, and "0" is a real answer for a free plan -
  // so only an absent value falls back to a dash.
  const planPrice =
    data?.plan_purchase_price !== undefined &&
    data?.plan_purchase_price !== null &&
    data?.plan_purchase_price !== ""
      ? `₹${Number(data.plan_purchase_price).toFixed(2)}`
      : null;

  const offerings = data?.current_offering?.offerings || [];

  return createPortal(
    <div className="patient-modal-overlay" onClick={() => setShowModal(false)}>
      <div className="patient-modal" onClick={(e) => e.stopPropagation()}>
        <div className="patient-modal-hero">
          <div className="patient-modal-avatar">
            {patientName !== "No patient loaded" ? getInitials(patientName) : "—"}
          </div>
          <div className="patient-modal-hero-meta">
            <div className="patient-modal-name">{patientName}</div>
            <div className="patient-modal-sub">
              {subParts.map((part, i) => (
                <React.Fragment key={part}>
                  {i > 0 && <span className="sep">·</span>}
                  <span>{part}</span>
                </React.Fragment>
              ))}
              {planStatus && (
                <span
                  className={`pill-status ${
                    planStatus === "Expired"
                      ? "expired"
                      : planStatus === "Paused"
                        ? "paused"
                        : planStatus === "Not started"
                          ? "pending"
                          : ""
                  }`}
                >
                  {planStatus}
                </span>
              )}
            </div>
          </div>
          <button
            className="patient-modal-close"
            onClick={() => setShowModal(false)}
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="patient-modal-body">
          <PatientConditionCard
            editing={profile.editing}
            options={profile.comorbidOptions}
            glp1={profile.glp1}
            comorbidities={profile.comorbidities}
            healthGoal={profile.healthGoal}
            medications={profile.medications}
            allergies={profile.allergies}
            sharedLog={profile.sharedLog}
            patient={{
              name: patientName !== "No patient loaded" ? patientName : "",
              age: patientAge,
              sex: patientGender,
              phone: phoneDisplay,
              email: data?.email,
            }}
            saving={profile.saving}
            onEdit={profile.startEdit}
            onCancel={profile.cancelEdit}
            onSave={async (draft) => {
              const ok = await profile.saveCondition(
                conditionDraftToAnswers(draft),
              );
              if (ok) onConditionSaved?.();
            }}
          />

          <div className="modal-card">
            <div className="mc-title">
              <IdCardIcon />
              Account
            </div>
            <div className="mc-rows">
              <InfoRow label="Patient ID" value={data?.patient_id} copyable />
              <InfoRow
                label="Phone"
                value={phoneDisplay}
                href={phoneHref}
                copyable
              />
              <InfoRow
                label="Email"
                value={data?.email}
                href={emailHref}
                copyable
              />
              <InfoRow label="City" value={data?.city} />
              <InfoRow label="Gender" value={patientGender} />
              <InfoRow label="Age" value={patientAge ? `${patientAge} years` : null} />
              <InfoRow label="Language" value={data?.language_name} />
            </div>
          </div>

          <div className="modal-card">
            <div className="mc-title">
              <ShieldIcon />
              Program &amp; Plan
            </div>
            <div className="mc-rows">
              <InfoRow label="Package" value={data?.plan_name} />
              <InfoRow
                label="Package duration"
                value={getPlanDuration(data?.plan_duration)}
              />
              <InfoRow label="Plan price" value={planPrice} />
              <InfoRow label="Plan start date" value={planStart} />
              <InfoRow label="Plan end date" value={planEnd} />
              <InfoRow label="Plan status" value={planStatus} />
            </div>
          </div>

          <div className="modal-card">
            <div className="mc-title">
              <PulseIcon />
              Clinical summary
            </div>
            <div className="mc-rows">
              <InfoRow label="Condition" value={condition} />
              <InfoRow
                label="Medical condition"
                value={data?.medical_condition_name}
              />
              <InfoRow label="Severity" value={data?.severity_name} />
              <InfoRow label="Current weight" value={weight} />
              <InfoRow label="Height" value={height} />
              <InfoRow label="BMI" value={bmi} />
            </div>
          </div>

          <div className="modal-card modal-card-wide">
            <div className="mc-title">
              <ListIcon />
              Offerings
            </div>
            {offerings.length > 0 ? (
              <CurrentOfferings offerings={offerings} />
            ) : (
              <div className="mc-empty">No offerings on this plan</div>
            )}
          </div>

        </div>
      </div>
    </div>,
    document.body,
  );
});

export default React.memo(PatientProfileModal);
